import { EFontWeights, EPaymentMethod } from '../Helper/enums';
import { PaymentModalProps, PaymentMethodData, PaymentData } from './types';
import Modal from '../Modal';
import PaymentMethod from './PaymentMethod';
import { StyledPaymentButtonsContainer } from './style';
import { useApplePay } from '../Helper/utils';
import { useState } from 'react';
import { CircularProgress, Typography } from '@mui/material';
import { makeOrder } from '../../hooks/useApi';
import PaypalWrapper from './PaypalWrapper';

function PaymentModal({
    headerText,
    headerColor,
    headerSize,
    text,
    textColor,
    textSize,
    textWeight = EFontWeights.Normal,
    closeModal,
    paymentClicked,
    modalColor,
    modalBorderColor,
    buttonColor,
    buttonTextColor,
    preview,
    fontFamily,
    paymentMethods,
    paypalButton,
    showOverlay = true,
    showCloseButton = true,
    playerId,
    offerId,
    currencyCode,
    utmSource,
    isStandAlone = false,
    token,
    redirectUrl,
    onOrderSuccess,
    onOrderFailed,
    onOrderSettled,
    sessionToken,
    bgImage,
    price,
    visible
}: PaymentModalProps) {
    const { isApplePayAvailable } = useApplePay();
    const [processing, setProcessing] = useState(false);

    const _paymentClicked = (paymentMethodName: EPaymentMethod) => {
        setProcessing(true);
        if (isStandAlone) {
            window.open(
                `${redirectUrl}/redirect/?pm=${paymentMethodName}&s=${sessionToken}&b=${bgImage}&p=${price}`,
                '_blank'
            );
        } else {
            if (!offerId || !currencyCode) return;
            const currency = currencyCode;
            const paymentData: PaymentData = {
                data: {
                    clientGaId:
                        localStorage.getItem('ga_client_id') || '2134654321',
                    currency: currency.toLowerCase(),
                    paymentProvider: 'stripe',
                    paymentMethod: paymentMethodName,
                    utmSource: utmSource || 'null',
                    offerId: offerId,
                    sequence: 1
                }
            };
            makeOrder(paymentData.data, token || '')
                .then((res) => {
                    onOrderSuccess?.(res);
                })
                .catch((err) => {
                    onOrderFailed?.(err);
                })
                .finally(() => {
                    onOrderSettled?.();
                });
        }
    };

    const customButton = (paymentMethodData: PaymentMethodData) => {
        return (
            <PaymentMethod
                buttonText={paymentMethodData.paymentMethodDisplayName}
                key={paymentMethodData.paymentMethodName}
                checkoutWithCard={() => {
                    paymentClicked?.();
                    _paymentClicked(paymentMethodData.paymentMethodName);
                }}
                buttonColor={buttonColor}
                buttonTextColor={buttonTextColor}
                icon={paymentMethodData.paymentMethodAssetUrl}
            />
        );
    };

    const applePayButton = (
        <div
            className="button"
            key={EPaymentMethod.APPLE}
            onClick={() => {
                if (isStandAlone) {
                    setProcessing(true);
                    window.open(
                        `${redirectUrl}/redirect/?pm=${EPaymentMethod.APPLE}&s=${sessionToken}&b=${bgImage}&p=${price}`,
                        '_blank'
                    );
                }
                paymentClicked(EPaymentMethod.APPLE);
            }}
        >
            <img
                alt="apple pay"
                src="https://media.appcharge.com/defaults/apple_pay_white.svg"
            />
        </div>
    );

    const googlePayButton = (
        <div
            className="button"
            key={EPaymentMethod.GOOGLE}
            onClick={() => {
                if (isStandAlone) {
                    setProcessing(true);
                    window.open(
                        `${redirectUrl}/redirect/?pm=${EPaymentMethod.GOOGLE}&s=${sessionToken}&b=${bgImage}&p=${price}`,
                        '_blank'
                    );
                }
                paymentClicked(EPaymentMethod.GOOGLE);
            }}
        >
            <img
                alt="google pay"
                src="https://media.appcharge.com/defaults/google_pay_white.svg"
            />
        </div>
    );

    const paymentMethodsButtons = {
        apple: isApplePayAvailable
            ? applePayButton
            : preview
            ? applePayButton
            : null,
        google: !isApplePayAvailable
            ? googlePayButton
            : preview
            ? googlePayButton
            : null,
        paypal: <PaypalWrapper preview={preview} paypalButton={paypalButton} key="paypal"/>,
    };

    return (
        <Modal
            title={!processing ? headerText : ''}
            titleColor={headerColor}
            titleSize={headerSize}
            titleWeight={textWeight}
            subtitle={!processing ? text : ''}
            subtitleColor={textColor}
            subtitleSize={textSize}
            closeModal={closeModal}
            modalColor={modalColor}
            modalBorderColor={modalBorderColor}
            fontFamily={fontFamily}
            preview={preview}
            showOverlay={showOverlay}
            showCloseButton={showCloseButton}
            visible={visible}
        >
            {processing ? (
                <>
                    <CircularProgress
                        sx={{ color: 'white', marginBottom: '2em' }}
                        size={'4em'}
                    />
                    <Typography variant="h5" sx={{ color: 'white' }}>
                        Processing...
                    </Typography>
                </>
            ) : (
                <StyledPaymentButtonsContainer>
                    {paymentMethods.map((paymentMethod: PaymentMethodData) => {
                        if (
                            paymentMethod.paymentMethodName ===
                                EPaymentMethod.CARD ||
                            paymentMethod.paymentMethodName ===
                                EPaymentMethod.E_WALLET
                        ) {
                            return customButton(paymentMethod);
                        } else {
                            return paymentMethodsButtons[
                                paymentMethod.paymentMethodName
                            ];
                        }
                    })}
                </StyledPaymentButtonsContainer>
            )}
        </Modal>
    );
}

export default PaymentModal;
